import { useMemo, useCallback } from 'react';
import { useTransactions } from '@/contexts/transactions';
import { useCategories } from '@/contexts/categories';
import { useAccounts } from '@/contexts/accounts';
import { useCards } from '@/contexts/cards';
import { useBudgets } from '@/contexts/budgets';
import { useGoals } from '@/contexts/goals';
import { useFutureReleases } from '@/contexts/future-releases';
import { useDebts } from '@/contexts/debts';
import { formatCurrency } from '@/utils/formatCurrency';

export interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  type: 'transaction' | 'category' | 'account' | 'card' | 'budget' | 'goal' | 'future_release' | 'debt';
  tag: string;
  data?: any;
}

export const useEntitiesSearch = () => {
  const { transactions } = useTransactions();
  const { cards } = useCards();
  const { categories } = useCategories();
  const { futureReleases } = useFutureReleases();
  const { debts } = useDebts();
  const { accounts } = useAccounts();
  const { budgets } = useBudgets();
  const { goals } = useGoals();

  const searchEntities = useCallback((query: string): SearchResult[] => {
    if (!query || query.length < 2) return [];

    const results: SearchResult[] = [];
    const lowerQuery = query.toLowerCase();

    // Search transactions
    transactions
      .filter(t => !t.deleted)
      .forEach(transaction => {
        if (
          transaction.description?.toLowerCase().includes(lowerQuery) ||
          transaction.category?.toLowerCase().includes(lowerQuery)
        ) {
          results.push({
            id: transaction.id,
            title: transaction.description || 'Transação sem descrição',
            subtitle: `${transaction.category} • ${formatCurrency(transaction.amount)}`,
            type: 'transaction' as const,
            tag: 'Transação',
            data: transaction
          });
        }
      });

    // Search cards
    cards.forEach(card => {
      if (
        card.name.toLowerCase().includes(lowerQuery) ||
        card.bank?.toLowerCase().includes(lowerQuery)
      ) {
        results.push({
          id: card.id,
          title: card.name,
          subtitle: `${card.bank || 'Banco não informado'} • Cartão`,
          type: 'card' as const,
          tag: 'Cartão',
          data: card
        });
      }
    });

    // Search categories
    categories.forEach(category => {
      if (category.name.toLowerCase().includes(lowerQuery)) {
        results.push({
          id: category.id,
          title: category.name,
          subtitle: category.type === 'receita' ? 'Receita' : 'Despesa',
          type: 'category' as const,
          tag: 'Categoria',
          data: category
        });
      }
    });

    // Search future releases
    futureReleases.forEach(release => {
      if (
        release.name.toLowerCase().includes(lowerQuery) ||
        release.description?.toLowerCase().includes(lowerQuery)
      ) {
        results.push({
          id: release.id,
          title: release.name,
          subtitle: `Lançamento Futuro • ${formatCurrency(release.amount)}`,
          type: 'future_release' as const,
          tag: 'Futuro',
          data: release
        });
      }
    });

    // Search debts
    debts.forEach(debt => {
      if (debt.name.toLowerCase().includes(lowerQuery)) {
        const totalAmount = debt.totalAmount || 0;
        results.push({
          id: debt.id,
          title: debt.name,
          subtitle: `Dívida • ${formatCurrency(totalAmount)}`,
          type: 'debt' as const,
          tag: 'Dívida',
          data: debt
        });
      }
    });

    // Search accounts
    accounts.forEach(account => {
      if (
        account.name.toLowerCase().includes(lowerQuery) ||
        account.bank?.toLowerCase().includes(lowerQuery)
      ) {
        results.push({
          id: account.id,
          title: account.name,
          subtitle: `${account.bank || 'Banco não informado'} • ${formatCurrency(account.balance)}`,
          type: 'account' as const,
          tag: 'Conta',
          data: account
        });
      }
    });

    // Search budgets
    budgets.forEach(budget => {
      const category = categories.find(c => c.id === budget.categoryId);
      if (category && category.name.toLowerCase().includes(lowerQuery)) {
        results.push({
          id: budget.id,
          title: `Orçamento - ${category.name}`,
          subtitle: `${formatCurrency(budget.spent)} de ${formatCurrency(budget.amount)}`,
          type: 'budget' as const,
          tag: 'Orçamento',
          data: budget
        });
      }
    });

    // Search goals
    goals.forEach(goal => {
      if (goal.name.toLowerCase().includes(lowerQuery)) {
        results.push({
          id: goal.id,
          title: goal.name,
          subtitle: `Meta • ${formatCurrency(goal.currentValue)} de ${formatCurrency(goal.targetValue)}`,
          type: 'goal' as const,
          tag: 'Meta',
          data: goal
        });
      }
    });

    return results.slice(0, 20); // Limit results
  }, [transactions, cards, categories, futureReleases, debts, accounts, budgets, goals]);

  return { searchEntities };
};
